import httpclient from 'libs/Axios';
import { extractGeneratedDescription } from 'features/DigitalMarketing/utils/apiResponse';

export type TestimonialStatus = 'Live' | 'Draft';

export type Testimonial = {
    id: number;
    parentFeedbackId?: number;
    clientName: string;
    companyName: string;
    feedback: string;
    rating: number;
    date: string;
    status: TestimonialStatus;
    avatar: string;
};

export type TestimonialPayload = {
    PARENT_FEEDBACK_ID?: number;
    NAME: string;
    DESCRIPTION: string;
    RELATION: string;
    PARENT_FEEDBACK_RATING: number;
    PHOTO_URL: string;
};

export type TestimonialsListResponse = {
    items: Testimonial[];
    total: number;
    pageIndex: number;
    pageSize: number;
};

const formatTestimonialDate = (value?: string) => {
    if (!value || value === 'None') {
        return '';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString('en-GB');
};

const mapApiTestimonial = (item: any, index: number): Testimonial => {
    const parentFeedbackId = Number(item?.PARENT_FEEDBACK_ID || item?.parent_feedback_id || item?.id || index);

    return {
        id: parentFeedbackId || index,
        parentFeedbackId: parentFeedbackId || undefined,
        clientName: item?.NAME || item?.name || '',
        companyName: item?.RELATION || item?.relation || '',
        feedback: item?.DESCRIPTION || item?.description || '',
        rating: Number(item?.PARENT_FEEDBACK_RATING || item?.parent_feedback_rating || 0),
        date: formatTestimonialDate(item?.CREATED_DATETIME || item?.created_datetime),
        status: item?.IS_PUBLISHED_WEBSITE || item?.is_published_website ? 'Live' : 'Draft',
        avatar: item?.PHOTO_URL || item?.photo_url || '',
    };
};

export const testimonialsApi = {
    async list(pageIndex = 1, pageSize = 10): Promise<TestimonialsListResponse> {
        const response = await httpclient().get(`/data/branch/testimonials/list?page_index=${pageIndex}&page_size=${pageSize}&sort_by=CREATED_DATETIME&sort_type=desc`);
        const responseData = response?.data?.data;
        const testimonialList = Array.isArray(responseData?.TESTIMONIALS)
            ? responseData.TESTIMONIALS
            : Array.isArray(responseData)
                ? responseData
                : [];

        return {
            items: testimonialList.map(mapApiTestimonial),
            total: Number(responseData?.Total || responseData?.TOTAL || responseData?.total || testimonialList.length || 0),
            pageIndex: Number(responseData?.PAGE_INDEX || responseData?.page_index || pageIndex),
            pageSize: Number(responseData?.PAGE_SIZE || responseData?.page_size || pageSize),
        };
    },

    async delete(parentFeedbackId: number) {
        return httpclient().delete(`/data/branch/testimonials/delete?parent_feedback_id=${parentFeedbackId}`);
    },

    async publish(parentFeedbackId: number) {
        return httpclient().post(`/data/branch/testimonials/publish?parent_feedback_id=${parentFeedbackId}`);
    },

    async improveDescription(description: string) {
        const response = await httpclient().post('/data/branch/testimonials/improve_ai', {
            DESCRIPTION: description,
        });

        return extractGeneratedDescription(response);
    },

    async upsert(payload: TestimonialPayload) {
        return httpclient().post('/data/branch/testimonials/upsert', payload);
    },
};
